import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Badge, Button, ConfirmDialog, Modal, Tabs } from '../../../school_panel/components/ui';
import { SchoolApi } from '../../../school_panel/api/client';
import AttachmentsField from '../../../school_panel/components/AttachmentsField';
import { useArchiveTask, useDeleteTaskAttachment, useUploadTaskAttachments } from '../../../school_panel/api/hooks';
import type { Attachment, StaffSchoolTask } from '../../../school_panel/api/types';
import { taskChips } from '../taskChips';
import TaskTrackerBody from './TaskTrackerBody';
import { withTime } from '../../../school_panel/lib/dates';

function serverDetail(err: unknown): string | null {
  const d = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof d === 'string' && d ? d : null;
}

interface Props {
  task: StaffSchoolTask | null;
  onClose: () => void;
}

export default function NoticeDetailModal({ task, onClose }: Props) {
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<'notice' | 'readers'>('notice');
  const archive = useArchiveTask();
  const upload = useUploadTaskAttachments();
  const delAtt = useDeleteTaskAttachment();
  const [atts, setAtts] = useState<Attachment[]>(task?.attachments ?? []);
  const [attToRemove, setAttToRemove] = useState<Attachment | null>(null);
  const canArchive = !!task && task.can_archive;

  // A different notice (or reopening) starts clean: no stale error or dialog.
  useEffect(() => { setError(null); setConfirming(false); setTab('notice'); setAttToRemove(null); }, [task?.id]);
  // Follow the list data (after a refetch) as well as a different notice.
  useEffect(() => { setAtts(task?.attachments ?? []); }, [task?.id, task?.attachments]);

  function addFiles(files: File[]) {
    if (!task) return;
    upload.mutate({ taskId: task.id, files }, {
      onSuccess: (r) => {
        setAtts(r.attachments);
        toast.success('Files added');
      },
      onError: (e) => toast.error(serverDetail(e) ?? "The files couldn't be uploaded. Try again."),
    });
  }
  function removeAttachment() {
    if (!task || !attToRemove) return;
    const gone = attToRemove;
    delAtt.mutate({ taskId: task.id, id: gone.id }, {
      onSuccess: () => { setAtts((cur) => cur.filter((a) => a.id !== gone.id)); toast.success('File removed'); },
      onError: (e) => toast.error(serverDetail(e) ?? "Couldn't remove the file. Try again."),
      onSettled: () => setAttToRemove(null),
    });
  }

  async function doArchive() {
    if (!task || archive.isPending) return;
    try {
      await archive.mutateAsync(task.id);
      setConfirming(false);
      onClose();
    } catch {
      setConfirming(false);
      setError('Could not archive the notice. Try again.');
    }
  }

  return (
    <>
      <Modal
        isOpen={!!task && !confirming && !attToRemove}
        onClose={onClose}
        title={task?.title}
        footer={
          <>
            <Button variant="ghost" onClick={onClose}>Close</Button>
            {canArchive && tab === 'notice' && <Button variant="danger" onClick={() => { setError(null); setConfirming(true); }}>Archive</Button>}
          </>
        }
      >
        {task && (
          <>
          <Tabs
            label="Notice sections"
            items={[{ key: 'notice', label: 'Notice' }, { key: 'readers', label: "Who's read it" }]}
            value={tab}
            onChange={setTab}
          />
          {tab === 'readers' ? <TaskTrackerBody taskId={task.id} /> : (
          <div className="space-y-3">
            {error && <p role="alert" className="text-sm text-danger-ink">{error}</p>}
            <p className="whitespace-pre-line text-sm text-ink">{task.body}</p>
            <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted">
              <Badge tone="primary">{task.audience_label}</Badge>
              <span>Sent {withTime(task.opens_at, { day: 'numeric', month: 'short' })}</span>
              {taskChips(task).map((c) => <Badge key={c} tone="neutral">{c}</Badge>)}
            </div>
            {(atts.length > 0 || canArchive) && (
              <div>
                <h3 className="mb-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">Attachments</h3>
                <AttachmentsField
                  attachments={atts}
                  readOnly={!canArchive}
                  busy={upload.isPending}
                  disabled={delAtt.isPending}
                  onOpen={(a) => { SchoolApi.downloadAttachment(a).catch(() => toast.error("Couldn't open the file. Try again.")); }}
                  onAdd={addFiles}
                  onRemove={(item) => { if (!(item instanceof File)) setAttToRemove(item); }}
                />
              </div>
            )}
          </div>
          )}
          </>
        )}
      </Modal>
      <ConfirmDialog
        isOpen={confirming}
        title="Archive this notice? Students will no longer see it."
        confirmLabel="Confirm"
        busy={archive.isPending}
        onConfirm={doArchive}
        onCancel={() => setConfirming(false)}
      />
      <ConfirmDialog
        isOpen={!!attToRemove}
        title={`Remove ${attToRemove?.name ?? ''}?`}
        confirmLabel="Remove"
        busy={delAtt.isPending}
        onCancel={() => setAttToRemove(null)}
        onConfirm={removeAttachment}
      />
    </>
  );
}
